/**
 * @file useOpenSlot.ts
 * @description Открыть место для секций блога в конструкторе (phase19.16
 * Р2; §16.5; uiux-design-r2 §8): страница места (создаётся сервером при
 * первом обращении) открывается в структуре; без выбранного блока сразу
 * открывается «Добавить блок» (по желанию — с группой заготовок первой).
 * Переход мог отменить человек (несохранённый блок) — тогда ничего.
 */

import type { SectionPresetGroup, SiteSlotKey } from "@chaiberi/shared";
import { useCallback, useContext } from "react";

import { BuilderContext } from "../../components/site-builder/builder-context";
import { useNotify } from "../../components/ui/NotifyProvider";
import { sitePages } from "../../lib/api-site-pages";
import { blockErrorText } from "../../lib/site-builder/block-errors";

export interface OpenSlotOptions {
  /** Открыть сразу этот блок места (иначе — окно заготовок). */
  blockId?: string;
  /** id страницы места, если уже известен. */
  pageId?: string | null;
  group?: SectionPresetGroup;
}

/** `null` — вне конструктора (контекста нет): открыть место негде. */
export function useOpenSlot(): ((slot: SiteSlotKey, options?: OpenSlotOptions) => Promise<void>) | null {
  const b = useContext(BuilderContext);
  const notify = useNotify();
  const open = useCallback(
    async (slot: SiteSlotKey, options: OpenSlotOptions = {}) => {
      if (!b) return;
      try {
        const pageId = options.pageId ?? (await sitePages.slot(slot)).id;
        const sel = options.blockId ? ({ kind: "block", id: options.blockId } as const) : ({ kind: "page" } as const);
        const moved = await b.select({ pageId, screen: null, panel: null, sel });
        if (moved && !options.blockId) b.openLibrary(undefined, { group: options.group });
      } catch (err) {
        notify.error(blockErrorText(err, "Не удалось открыть место для блоков."));
      }
    },
    [b, notify],
  );
  return b ? open : null;
}
